<template>
  <div class="rs-radio">
    <input
      :id="id"
      :disabled="disabled"
      type="radio"
      :checked="shouldBeChecked"
      :value="value"
      :style="label ? `margin: 0 1rem 0.5rem 0;` : `margin: 0`"
      @click="updateInput"
    >
    <label
      :for="id"
      :class="`${label ? 'radio-pl-wide' : 'radio-pl-narrow'} ${variant} `"
      :style="`cursor: ${labelCursor};`"
    >
      <slot name="label">
        <span
          v-if="label"
          class="label"
          :style="normalFont ? 'fontWeight:500' : 'fontWeight:700'"
        >{{ label }}
        </span>
      </slot>
    </label>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

type TRadioValue = string | number | boolean | Record<string, unknown> | null | undefined;

export default defineComponent({
  name: 'RsFormRadio',
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    rules: [String],

    id: [String],
    label: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Boolean, Object] as PropType<TRadioValue>,
      default: undefined,
    },
    modelValue: {
      type: [String, Number, Boolean, Object] as PropType<TRadioValue>,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: '',
    },
    normalFont: {
      type: Boolean,
      default: false,
    },
    /**
     * Allows for overriding the cursor pointer of a radio button instance.
     * Value can be of any valid cursor CSS value listed in this document
     * https://developer.mozilla.org/en-US/docs/Web/CSS/cursor.
     */
    labelCursor: {
      type: String,
      default: 'pointer',
    },
  },
  data() {
    return {
      valid: null,
    };
  },
  computed: {
    shouldBeChecked(): boolean {
      return typeof this.modelValue === 'object'
        ? JSON.stringify(this.modelValue ?? {}) === JSON.stringify(this.value ?? {})
        : this.modelValue === this.value;
    },
  },
  methods: {
    updateInput(): void {
      this.$emit('change', this.value);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.radio-pl-narrow {
  padding-left: 16px;
}
.radio-pl-wide {
  padding-left: 28px;
}
.rs-radio {
  position: relative;
  min-height: 1.5rem;
  label {
    margin: 0;
    position: relative;
    .label {
      font-weight: normal;
      color: $tertiary-muted;
    }
    &:hover {
      cursor: pointer;
      &:after {
        transform: scale(3);
      }
    }
    &:before {
      content: '';
      position: absolute;
      top: 1px;
      left: 0;
      display: block;
      width: 16px;
      height: 16px;
      pointer-events: none;
      background-color: #fff;
      border-radius: 50%;
      border: 2px solid $tertiary-gray;
    }

    &:after {
      content: '';
      position: absolute;
      top: 5px;
      left: 4px;
      width: 8px;
      height: 8px;
      border-radius: 100%;
      background: $secondary-color;
      transform: scale(0);
      transition: all 0.2s ease;
      opacity: 0.08;
      pointer-events: none;
    }

    &.disabled-circle:hover {
      cursor: default;
    }

    &.disabled-circle:before {
      border-color: $storm-grey !important;
      background-color: $storm-grey !important;
      box-shadow: 0 0 0 2px #CBD1E0;
      width: 14px;
      height: 14px;
    }

    &.disabled-circle:after{
      background-color: unset !important;
    }
  }
  input[type='radio'] {
    display: none;
    &:checked ~ label {
      &:before {
        border-color: $secondary-color;
      }
      &:after {
        transform: scale(1);
        transition: all 0.2s cubic-bezier(0.35, 0.9, 0.4, 0.9);
        opacity: 1;
      }
    }
    &:disabled:not(:checked) ~ label {
      &:before {
        background-color: rgba($tertiary-gray, 0.3);
      }
      &:after {
        transform: scale(0);
      }
    }
    &:disabled:checked ~ label {
      &:before {
        border-color: $tertiary-gray;
        background-color: rgba($tertiary-gray, 0);
      }
      &:after {
        transform: scale(1);
        transition: all 0.2s cubic-bezier(0.35, 0.9, 0.4, 0.9);
        opacity: 1;
        background-color: darken($tertiary-gray, 10%);
      }
    }
  }
}
</style>
